/**
 * Firebase Wishlist Service
 * Manages per-user wishlists in Firestore (stores product IDs only)
 */
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  Timestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db, COLLECTIONS } from '@/config/firebase.config';

/**
 * Get wishlist product IDs for a user
 */
export const getWishlistProductIds = async (userId: string): Promise<string[]> => {
  try {
    const docSnap = await getDoc(doc(db, COLLECTIONS.WISHLISTS, userId));
    if (docSnap.exists()) {
      return (docSnap.data().productIds as string[]) || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching wishlist:', error);
    return [];
  }
};

/**
 * Add a product to the user's wishlist
 */
export const addToWishlistFB = async (userId: string, productId: string): Promise<void> => {
  try {
    await setDoc(
      doc(db, COLLECTIONS.WISHLISTS, userId),
      {
        userId,
        productIds: arrayUnion(productId),
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error adding to wishlist:', error);
  }
};

/**
 * Remove a product from the user's wishlist
 */
export const removeFromWishlistFB = async (userId: string, productId: string): Promise<void> => {
  try {
    await setDoc(
      doc(db, COLLECTIONS.WISHLISTS, userId),
      {
        productIds: arrayRemove(productId),
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error removing from wishlist:', error);
  }
};

/**
 * Set the full wishlist (used for merge-on-login)
 */
export const setWishlistFB = async (userId: string, productIds: string[]): Promise<void> => {
  try {
    await setDoc(
      doc(db, COLLECTIONS.WISHLISTS, userId),
      {
        userId,
        productIds,
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error setting wishlist:', error);
  }
};

/**
 * Clear the user's wishlist
 */
export const clearWishlistFB = async (userId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.WISHLISTS, userId));
  } catch (error) {
    console.error('Error clearing wishlist:', error);
  }
};
